import React from 'react';

const EventActivities = ({ 
  event, 
  activities, 
  activityLoading, 
  activityError, 
  newActivity, 
  setNewActivity, 
  handleActivitySubmit 
}) => {
  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold text-gray-900">Activities</h3>
        <div className="text-sm text-gray-500">
          {activities.length} {activities.length === 1 ? 'Activity' : 'Activities'}
        </div>
      </div>
      
      {/* Add Activity Form */}
      {(event.event.eventStatus === "In Progress" || event.event.eventStatus === "Upcoming") && (
        <form onSubmit={handleActivitySubmit} className="mb-8 bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Add New Activity</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Activity Title</label>
              <input
                type="text"
                value={newActivity.activityTitle}
                onChange={(e) => setNewActivity(prev => ({...prev, activityTitle: e.target.value}))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F15213] focus:border-[#F15213] transition-colors duration-200"
                placeholder="Enter activity title"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={newActivity.activityDescription}
                onChange={(e) => setNewActivity(prev => ({...prev, activityDescription: e.target.value}))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#F15213] focus:border-[#F15213] transition-colors duration-200"
                rows="3"
                placeholder="Describe the activity"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Activity Image</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-[#F15213] transition-colors duration-200">
                <div className="space-y-1 text-center">
                  {newActivity.activityImage ? (
                    <div className="space-y-2">
                      <img
                        src={URL.createObjectURL(newActivity.activityImage)}
                        alt="Activity preview"
                        className="mx-auto h-32 w-32 object-cover rounded-lg"
                      />
                      <p className="text-sm text-gray-600">{newActivity.activityImage.name}</p>
                      <button
                        type="button"
                        onClick={() => setNewActivity(prev => ({...prev, activityImage: null}))}
                        className="text-red-600 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-[#F15213] hover:text-[#d14711] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#F15213]"
                        >
                          <span>Upload a file</span>
                          <input
                            id="file-upload"
                            type="file"
                            accept="image/*"
                            onChange={(e) => setNewActivity(prev => ({...prev, activityImage: e.target.files[0]}))}
                            className="sr-only"
                            required
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </>
                  )}
                </div>
              </div>
            </div>
            <button
              type="submit"
              className="w-full bg-[#F15213] text-white py-3 rounded-lg hover:bg-[#d14711] transition-colors duration-200 font-medium flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Add Activity
            </button>
          </div>
        </form>
      )}

      {/* Activities List */}
      {activityLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F15213]"></div>
        </div>
      ) : activityError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-center">
          {activityError}
        </div>
      ) : activities.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {activities.map((activity) => (
            <div 
              key={activity._id} 
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-200"
            >
              {activity.activityImage && (
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={activity.activityImage}
                    alt={activity.activityTitle}
                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-200"
                  />
                </div>
              )}
              <div className="p-6">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="text-lg font-semibold text-gray-900">{activity.activityTitle}</h4>
                  <span className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                    {new Date(activity.activityTime).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-gray-600 mb-4 line-clamp-3">{activity.activityDescription}</p>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span className="flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {new Date(activity.activityTime).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No activities yet</h3>
          <p className="mt-2 text-gray-500">Add your first activity to get started!</p>
        </div>
      )}
    </div>
  );
};

export default EventActivities; 